import React from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { loginRequest, isAuthConfigured } from './authConfig';
import { IDLE_SIGN_OUT_KEY } from './IdleLogout';
import { clearPreviewScope } from './customerPreview';
import PulseMark from './PulseMark';
import { COPYRIGHT_NOTICE } from './legalNotice';
import DemoSignIn from './DemoSignIn';
import { getDemoToken, isDemoMode } from './demoSession';

// Wraps the whole app: nothing renders until the user is signed in. Matches CLAUDE.md's stated
// expectation that this is an internal app behind company SSO, not a partially-public one.
function SignInScreen() {
  const { instance } = useMsal();
  const [error, setError] = React.useState('');

  // Read in the initializer and cleared in an effect, rather than both at once: StrictMode renders
  // twice in dev, and a read-then-clear would swallow the flag before it ever rendered.
  const [signedOutForIdle] = React.useState(() => {
    try {
      return sessionStorage.getItem(IDLE_SIGN_OUT_KEY) === '1';
    } catch {
      return false; // private mode
    }
  });
  React.useEffect(() => {
    try {
      sessionStorage.removeItem(IDLE_SIGN_OUT_KEY);
    } catch {
      /* private mode */
    }
    // Reaching the sign-in screen at all means the session ended -- however it ended (button, idle
    // timeout, expired token). Signing back in should never land you in someone else's customer
    // view, so this is where "View as Customer" is guaranteed to reset.
    clearPreviewScope();
  }, []);

  async function handleSignIn() {
    setError('');
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', paddingTop: 96, textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginBottom: 16 }}>
        <PulseMark size={72} trim />
        <span style={{ fontFamily: 'Georgia', fontSize: 56, fontWeight: 'bold', color: 'var(--heading)' }}>
          Pulse
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 32 }}>
        Aumentum Technologies · Professional Services
      </div>
      {signedOutForIdle && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 8, fontSize: 13, textAlign: 'left',
          background: 'var(--light-bg)', border: '1px solid var(--border)', color: 'var(--text-mid)',
          lineHeight: 1.5,
        }}>
          You were signed out after two hours of inactivity. Sign in again to pick up where you
          left off.
        </div>
      )}
      <button
        className="btn btn-primary"
        style={{ width: '100%', padding: 14, fontSize: 14, justifyContent: 'center' }}
        onClick={handleSignIn}
      >
        Sign in with your company account
      </button>
      {error && (
        <div style={{
          marginTop: 16, padding: '12px 16px', borderRadius: 8, fontSize: 13,
          background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)', color: 'var(--red)',
        }}>⚠ {error}</div>
      )}
      <div style={{ marginTop: 24, fontSize: 13, color: 'var(--text-mid)' }}>
        Signing in as a customer contact?{' '}
        <a href="/portal" style={{ color: 'var(--heading)' }}>Go to the customer portal</a>
      </div>
      <div style={{ marginTop: 32, fontSize: 10, lineHeight: 1.4, color: 'var(--text-mid)', opacity: 0.6 }}>
        {COPYRIGHT_NOTICE}
      </div>
    </div>
  );
}

// Shown when REACT_APP_ENTRA_* aren't set — a clear explanation beats MSAL's opaque startup error.
function NotConfiguredScreen() {
  return (
    <div style={{ maxWidth: 560, margin: '0 auto', paddingTop: 96 }}>
      <div className="card">
        <div className="card-title">Sign-in isn't configured</div>
        <div style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>
          <code>REACT_APP_ENTRA_TENANT_ID</code> and <code>REACT_APP_ENTRA_CLIENT_ID</code> are
          missing from this build. Set them in <code>apps/web/.env</code> (see{' '}
          <code>.env.example</code>) and restart the dev server, or add them as build environment
          variables for a deployed environment.
        </div>
      </div>
    </div>
  );
}

export default function AuthGate({ children }) {
  if (isDemoMode) return getDemoToken() ? children : <DemoSignIn />;
  if (!isAuthConfigured) return <NotConfiguredScreen />;

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInScreen />
      </UnauthenticatedTemplate>
    </>
  );
}
